<script setup lang="ts">
import { SplideSlide } from '@splidejs/vue-splide'

const props = defineProps<{
  title: StoryBlokHeading
  subtitle?: string
  type?: 'new' | 'old'
  items: {
    text: StoryBlokRichText
    authorName: string
    authorRole: string
    authorImage: StoryBlokImage
    rating?: string
  }[]
  itemShadow?: boolean
  link?: StoryBlokLink
  linkText?: string
  injectedVariables?: KeyValuePair[]
}>()

const sliderOptions = {
  type: 'loop',
  perPage: 2,
  fixedWidth: props.itemShadow ? '580px' : '570px',
  breakpoints: {
    1160: {
      perPage: 2,
      padding: !props.itemShadow && '20px',
    },
    800: {
      perPage: 2,
      padding: !props.itemShadow && '20px',
      fixedWidth: '413.33px',
    },
    640: {
      perPage: 1,
      padding: !props.itemShadow && '30px',
      fixedWidth: '315px',
      gap: '10px',
    },
  },
}

const injectedTitle = findTitleInArray(props.injectedVariables)
if (injectedTitle) props.title[0].text = injectedTitle
</script>

<template>
  <div class="w-full">
    <StoryblokHeading class="mx-5 sm:text-center" :title="title" />
    <div v-if="subtitle" class="text-large mt-10px mx-5 sm:text-center">
      {{ subtitle }}
    </div>
    <div class="mt-6">
      <AppSlider :options="sliderOptions" :item-shadow="itemShadow">
        <SplideSlide v-for="item in items">
          <div v-editable="item" class="splide__slide--testimonial" :class="{ 'bg-white': !props.itemShadow }">
            <div class="h-full rounded-xl" :class="{ 'item-shadow p-4 bg-white': props.itemShadow }">
              <div v-if="type === 'new'" class="h-full">
                <div class="h-full flex flex-col justify-between">
                  <BaseStarRating v-if="item.rating" :rating="item.rating" style-star-size="20" class="mb-3 w-min" style-full-star-color="#E7AB00" />
                  <StoryblokRichText class="mb-10 text-large flex-auto" :document="item.text" />
                  <div class="flex gap-5px items-center">
                    <StoryblokImage v-if="item.authorImage.filename" class="rounded-xl" :src="item.authorImage" width="40" height="40" />
                    <div class="flex flex-col">
                      <span class="headline-4">{{ item.authorName }}</span>
                      <span>{{ item.authorRole }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>
                <div class="flex gap-5px items-center">
                  <StoryblokImage v-if="item.authorImage.filename" class="rounded-xl" :src="item.authorImage" width="40" height="40" />
                  <div class="flex flex-col">
                    <span class="headline-4">{{ item.authorName }}</span>
                    <span>{{ item.authorRole }}</span>
                  </div>
                </div>
                <StoryblokRichText class="mt-10 text-large flex-auto" :document="item.text" />
              </div>
            </div>
          </div>
        </SplideSlide>
      </AppSlider>
    </div>
    <div v-if="link?.cached_url || link?.url" class="text-center mt-6">
      <BaseButton icon-end="fas-arrow-right" icon-size="small" render="link">
        <StoryblokLink target="_self" v-bind="link">
          {{ linkText }}
        </StoryblokLink>
      </BaseButton>
    </div>
  </div>
</template>

<style scoped>
.splide__slide--testimonial {
  @apply flex border-transparent flex-col justify-between transition p-5 relative rounded-xl h-full
  sm:w-full;
}

.item-shadow {
  box-shadow: 0 0 22px rgba(32, 28, 70, 0.05);
}
</style>
